<template>

  <div>

    <div :id="_uid" class="ui large modal" :class="modalSelector">
      <div class="ui header">
        {{ isCreating ? "Novo Título a Receber" : "Alterar Título a Receber" }}
      </div>
      <div class="content">
        <div class="ui form" :class="{ loading }">
          <div class="fields">
            <div class="six wide field">
              <label class="form-label">Cliente <span class="red text">*</span></label>
              <customer-picker ref="customerPicker" @onClear="onClearCustomer" @onPick="onPickCustomer" />
            </div>
            <div class="six wide field">
              <label class="form-label">Natureza <span class="red text">*</span></label>
              <select-dropdown ref="natureDropdown" @onChange="onChangeNature" />
            </div>
            <div class="four wide field">
              <label class="form-label">Tipo <span class="red text">*</span></label>
              <select-dropdown ref="typeDropdown" @onChange="onChangeType" />
            </div>
          </div>
          <div class="fields">      
            <div class="four wide field">
              <label class="form-label">Emissão <span class="red text">*</span></label>
              <date-picker ref="datePicker" :autoInit="false" @onChange="onChangeDate" />
            </div>     
            <div class="four wide field">
              <label class="form-label">Vencimento <span class="red text">*</span></label>
              <date-picker ref="dueDatePicker" :autoInit="false" @onChange="onChangeDueDate" />
            </div>                                     
            <div class="four wide field">
              <label class="form-label">Venc. Real <span class="red text">*</span></label>
              <date-picker ref="realDueDatePicker" :autoInit="false" @onChange="onChangeRealDueDate" />
            </div>  
            <div class="four wide field">
              <label class="form-label">Valor <span class="red text">*</span></label>
              <input class="money" :value="receivableValue" @blur="receivableValue = $event.target.value">
            </div>
          </div>
          <div class="fields">
            <div class="sixteen wide field">
              <label class="form-label">Observações</label>
              <input type="text" v-model="receivable.comments" />
            </div>
          </div>
        </div>
      </div>
      <div class="actions">
        <button class="ui cancel basic button">
          <i class="times icon"></i>
          Cancelar
        </button>      
        <button v-if="isUpdating" class="ui basic red button" 
          :class="{ loading: checkingRemoval || removing, disabled: saving }" @click="onRemove()">
          <i class="trash icon"></i> Excluir
        </button>
        <button class="ui blue button" :class="{ loading: saving, disabled: !canConfirm }" @click="onConfirm()">
          <i class="checkmark icon"></i>
          {{ isCreating ? "Confirmar" : "Atualizar" }}
        </button>
      </div>
    </div>

    <can-not-remove-modal ref="canNotRemoveModal" />

    <yes-no-modal ref="confirmRemovalModal" :message="`Confirma exclusão do título a receber ${receivable.id} ?`" 
      :danger="true" @onYes="onConfirmRemoval()"/>

  </div>
  
</template>

<script>

  import modalDialog from "@/mixins/modal-dialog"
  import ReceivableService from "@/services/receivable-service"
  import FinancialNatureService from "@/services/financial-nature-service"
  import { moment, Input } from "@/utils"
  import { CustomerPicker, DatePicker, SelectDropdown } from "@/components"

  export default {

    name: "ReceivableFormModal",

    mixins: [modalDialog],

    data() {
      return {
        receivable: {},
        service: new ReceivableService(),
      }
    },

    components: {
      CustomerPicker,
      DatePicker,
      SelectDropdown,
    },

    mounted() {

      Input.maskCurrency()

      this.loadNatures()
      
      this.$refs.typeDropdown.setValues(ReceivableService.getTypeList())
    },

    computed: {

      receivableValue: {
        get() {
          return Input.currencyToString(this.receivable.value)
        },
        set(value) {
          this.receivable.value = Input.stringToCurrency(value)    
          this.refreshData()   
        },
      },

      canConfirm() {
        return (
          this.receivable.customerId &&
          this.receivable.natureId &&
          this.receivable.type &&
          this.receivable.date &&
          this.receivable.dueDate &&
          this.receivable.realDueDate &&
          this.receivable.value
        )
      },
    },    
    
    methods: {

      async loadNatures() {

        this.loading = true

        try {

          const natures = await new FinancialNatureService().findAll({
            attributes: ["id", "type", "description"],
            order: "description",
          })

          const incomeNatures = natures.filter((nature) => nature.type == "income")

          this.$refs.natureDropdown.setValues(incomeNatures)
          
        } catch (error) {
          
          this.$errorHandler(error)
        
        } finally {

          this.loading = false
        }
      },

      load(receivable) {

        this.loading = true

        this.$refs.customerPicker.clear()
        this.$refs.natureDropdown.reset()
        this.$refs.typeDropdown.reset()
        this.$refs.datePicker.clearDate()
        this.$refs.dueDatePicker.clearDate()
        this.$refs.realDueDatePicker.clearDate()

        this.receivable = {
          type: "normal",
          date: new Date(),
        }                

        if (!receivable)
          return this.show()
        
        this.receivable = this.$cloneObject(receivable)
        
        this.receivable.date = moment(receivable.date).toDate()
        this.receivable.dueDate = moment(receivable.dueDate).toDate()
        this.receivable.realDueDate = moment(receivable.realDueDate).toDate()        

        this.show()
      },

      onVisible() {
        this.emitEvent = null
        this.initData()
        this.loading = false
      },      

      initData() {

        if (this.receivable.customer) {  
          this.receivable.customerId = this.receivable.customer.id
          this.$refs.customerPicker.setSelected(this.receivable.customer)
        }  
        
        this.$refs.natureDropdown.setSelected(this.receivable.natureId)
        this.$refs.typeDropdown.setSelected(this.receivable.type)        
                
        this.$refs.datePicker.initCalendar({ force: true })
        this.$refs.dueDatePicker.initCalendar({ force: true })
        this.$refs.realDueDatePicker.initCalendar({ force: true })
        
        this.$refs.datePicker.setDate(this.receivable.date)        

        if (this.receivable.dueDate)
          this.$refs.dueDatePicker.setDate(this.receivable.dueDate)

        if (this.receivable.realDueDate)  
          this.$refs.realDueDatePicker.setDate(this.receivable.realDueDate)
      },

      onPickCustomer(customer) {
        this.receivable.customerId = customer.id
        this.receivable.customer = customer
        this.refreshData()
      },

      onClearCustomer() {
        this.receivable.customerId = null
        this.refreshData()
      },

      onChangeNature(id) {
        this.receivable.natureId = id ? parseInt(id) : null
        this.refreshData()
      },

      onChangeType(id) {
        this.receivable.type = id
        this.refreshData()
      },

      onChangeDate(date) {
        this.receivable.date = date
        this.refreshData()          
      },

      onChangeDueDate(date) {
        
        this.receivable.dueDate = date

        if (!this.loading)
          this.$refs.realDueDatePicker.setDate(moment.getNextBusinessDay(date))

        this.refreshData()          
      },

      onChangeRealDueDate(date) {
        this.receivable.realDueDate = date
        this.refreshData()          
      },

      refreshData() {
        this.receivable = this.$cloneObject(this.receivable)
      },

      async onConfirm() {

        if (this.saving)
          return

        this.saving = true
        
        try {

          this.receivable.date = moment(this.receivable.date).format("YYYY-MM-DD")
          this.receivable.dueDate = moment(this.receivable.dueDate).format("YYYY-MM-DD")
          this.receivable.realDueDate = moment(this.receivable.realDueDate).format("YYYY-MM-DD")

          if (this.isCreating) {

            const newReceivable = await this.service.create(this.receivable)

            this.receivable.id = newReceivable.id

          } else {            

            await this.service.update(this.receivable)            
          }

          this.$reloadDataTables(["receivables"])

          this.emit("onSave", this.receivable)

          this.$msg.success(this.isCreating ? "Título criado com sucesso" : "Título alterado com sucesso")
          
        } catch (error) {

          this.$errorHandler(error)

        } finally {

          this.saving = false
        }
      },

      async onRemove() {

        if (this.receivable.invoiceId)
          return this.$msg.warn("Esse título não pode ser excluído porque foi gerado a partir de um fechamento")

        this.checkingRemoval = true

        try {

          const data = await this.service.canRemove(this.receivable)

          if (data.canRemove)  {
            this.$refs.confirmRemovalModal.show()              
          } else {
            this.$refs.canNotRemoveModal.show(data.entities)            
          }

        } catch (error) {

          this.$errorHandler(error)

        } finally {

          this.checkingRemoval = false
        }        
      },

      async onConfirmRemoval() {

        if (this.removing)
          return

        this.removing = true

        try {

          await this.service.remove(this.receivable) 

          this.$reloadDataTables(["receivables"])

          this.emit("onRemove", this.receivable)

          this.$msg.success("Título excluído com sucesso")
          
        } catch (error) {

          this.$errorHandler(error)

        } finally {

          this.removing = false
        }
      },
    },
  }

</script>